export const COPY_CODING_AGENT_LABEL = "Copy for coding agent";
export const COPY_CODING_AGENT_HELP =
  "If you use a coding agent on this product, paste this there. It describes the issue with enough context for the agent to help you address it.";
export const COPY_CODING_AGENT_COPIED = "Copied. Paste this into your coding agent.";

export type CodingAgentIssue = {
  title: string | null;
  description: string | null;
  suggestion: string | null;
  user_impact?: string | null;
  affected_pages?: Array<{ title: string | null; path: string; url?: string | null }>;
  viewports?: string[];
  screenshot?: { image_url: string | null } | null;
};

export function codingAgentPrompt(issue: CodingAgentIssue) {
  const sections = ["A usability review found this issue while using the product. Do not change any code yet."];
  const title = issue.title?.trim() || "Untitled issue";
  const description = issue.description?.trim();
  sections.push(`## What the visitor hits\n${description ? `${title}\n\n${description}` : title}`);

  const impact = issue.user_impact?.trim();
  if (impact) {
    sections.push(`## Impact\n${impact}`);
  }

  const suggestion = issue.suggestion?.trim();
  if (suggestion) {
    sections.push(
      `## Suggested direction\nTreat this as a UX hypothesis, not a spec. Check whether it fits the existing product before proposing a change.\n\n${suggestion}`,
    );
  }

  const where = whereSection(issue);
  if (where) {
    sections.push(where);
  }

  const screenshot = absoluteHttpUrl(issue.screenshot?.image_url ?? "");
  if (screenshot) {
    sections.push(`## Screenshot\n${screenshot}`);
  }

  sections.push(
    "## What to do\n1. Inspect the relevant UI and code in this app.\n2. Propose the smallest change that fits this product and addresses the issue, or explain why it should not be changed.\n3. Wait for confirmation before editing any code.",
  );

  return sections.join("\n\n");
}

function whereSection(issue: CodingAgentIssue) {
  const pageLines = (issue.affected_pages ?? []).map(pageLine).filter((line) => line != null);
  const viewports = (issue.viewports ?? []).map(viewportName).filter(Boolean);
  if (pageLines.length === 0 && viewports.length === 0) {
    return null;
  }

  const parts: string[] = [];
  if (pageLines.length > 0) {
    parts.push(pageLines.join("\n"));
  }
  if (viewports.length > 0) {
    parts.push(`Viewports: ${viewports.join(", ")}`);
  }

  return `## Where it showed up\n${parts.join("\n")}`;
}

function pageLine(page: { title: string | null; path: string; url?: string | null }) {
  const href = absoluteHttpUrl(page.url ?? "") || page.path.trim();
  if (!href) {
    return null;
  }

  const title = page.title?.trim();
  return title ? `- ${href} (${title})` : `- ${href}`;
}

function absoluteHttpUrl(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }

    return value;
  } catch {
    return null;
  }
}

function viewportName(value: string) {
  if (!value) {
    return "";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}
